import React from 'react';
import { Check, Star, ArrowRight } from 'lucide-react';
import { TrustBadges } from './TrustBadges';
declare global {
  interface Window {
    fbq: any;
  }
}
const PricingCard = ({
  title,
  price,
  description,
  features,
  cta,
  recommended = false,
  isPremium = false,
  image,
  checkoutLink,
  priceValue
}: {
  title: string;
  price: string;
  description: string;
  features: string[];
  cta: string;
  recommended?: boolean;
  isPremium?: boolean;
  image?: string;
  checkoutLink: string;
  priceValue: number;
}) => {
  const handleCheckoutClick = () => {
    // Track InitiateCheckout event
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        value: priceValue,
        currency: 'ARS',
        content_name: title,
        content_category: 'Yoga Esencial'
      });
    }
  };
  return <div className={`relative flex flex-col h-full rounded-2xl transition-all duration-300 ${recommended ? 'bg-white border-2 border-[#9CAF88] shadow-xl scale-105 z-10' : 'bg-white border border-gray-200 shadow-md hover:shadow-lg'}`} data-id="element-47">
      {recommended && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#9CAF88] text-white px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1 shadow-md" data-id="element-48">
          <Star size={14} fill="currentColor" data-id="element-49" />
          RECOMENDADO
        </div>}

      <div className="p-6 md:p-8 flex-1 flex flex-col" data-id="element-50">
        {image && <div className="mb-4 -mt-2" data-id="element-51">
            <img src={image} alt={title} className="w-full h-40 object-contain" data-id="element-52" />
          </div>}

        <h3 className="text-xl font-bold text-[#2D3436] mb-2 uppercase tracking-wide" data-id="element-53">
          {title}
        </h3>
        <div className="mb-4" data-id="element-54">
          <span className="text-4xl font-bold text-[#2D3436]" data-id="element-55">${price}</span>
        </div>
        <p className="text-gray-600 text-sm mb-6 min-h-[40px]" data-id="element-56">{description}</p>

        <div className="border-t border-gray-100 my-6" data-id="element-57"></div>

        <ul className="space-y-3 mb-8 flex-1" data-id="element-58">
          {features.map((feature, i) => <li key={i} className="flex items-start gap-3 text-sm" data-id="element-59">
              <div className={`mt-0.5 p-0.5 rounded-full ${recommended ? 'bg-[#9CAF88] text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-60">
                <Check size={12} strokeWidth={3} data-id="element-61" />
              </div>
              <span className="text-gray-700" data-id="element-62">{feature}</span>
            </li>)}
        </ul>

        <a href={checkoutLink} target="_blank" rel="noopener noreferrer" onClick={handleCheckoutClick} className={`w-full py-4 px-6 rounded-xl font-bold text-center transition-all duration-300 flex items-center justify-center gap-2 ${recommended ? 'bg-[#9CAF88] hover:bg-[#7A8C69] text-white shadow-lg hover:shadow-xl' : isPremium ? 'bg-[#2D3436] hover:bg-black text-white' : 'bg-[#FDF8F3] hover:bg-[#E8D5D5] text-[#2D3436] border border-[#E8D5D5]'}`} data-id="element-63">
          COMPRAR AHORA
          <ArrowRight size={18} data-id="element-64" />
        </a>
      </div>
    </div>;
};
export function Pricing() {
  return <section id="checkout" className="py-20 px-4 bg-[#FDF8F3]" data-id="element-65">
      <div className="max-w-7xl mx-auto" data-id="element-66">
        <div className="text-center mb-8" data-id="element-67">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-[#2D3436] mb-4" data-id="element-68">
            Elegí tu camino
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-69">
            Seleccioná el combo que mejor se adapte a tu objetivo actual.
          </p>
        </div>

        {/* Trust badge above combos */}
        <div className="flex flex-col items-center justify-center gap-3 mb-12" data-id="element-70">
          <p className="text-sm text-gray-500" data-id="element-71">
            Pago 100% seguro procesado por:
          </p>
          <img src="https://cdn.magicpatterns.com/uploads/d2sWTse3oPXYRCg4zx5fvq/ML.jpg" alt="Mercado Libre - Mercado Pago" className="h-10 object-contain" data-id="element-72" />
        </div>

        <div className="grid md:grid-cols-3 gap-8 items-start max-w-6xl mx-auto pt-4" data-id="element-73">
          {/* Combo 1 - Inicio Consciente */}
          <PricingCard title="Combo 1 – Inicio Consciente" price="19.990" priceValue={19990} description="Ideal si estás empezando o querés crear una rutina realista y sostenible desde casa." image="https://cdn.magicpatterns.com/uploads/4m5y9J4jDEXRvWwesSLa7R/(90_Dias)_Transforma_tu_habito_y_sostene_tu_motivacion.png" features={['Yoga Esencial – De cero a práctica consciente', 'Rutinas Express – 21 rutinas de Yoga (Gratis)', 'Yoga Terapéutico – Alivio físico y emocional (Gratis)', 'Diario de Práctica 90 Días (Gratis)', 'Acceso inmediato en PDF']} cta="COMPRAR AHORA" checkoutLink="https://checkout2.rosetadigital.com/cart/47014672072882:1" data-id="element-74" />

          {/* Combo 2 - Práctica Profunda */}
          <PricingCard title="Combo 2 – Práctica Profunda" price="29.990" priceValue={29990} description="Pensado para vos si querés avanzar un paso más, entender tu cuerpo y practicar con mayor seguridad." recommended={true} image="https://cdn.magicpatterns.com/uploads/1swnG7NYV7V9UyYdscJS2m/(90_Dias)_Transforma_tu_habilly_sostene_lu_motitation_dia_dia.png" features={['Todo lo del Combo 1 (Incluye los bonus gratis)', 'Anatomía y Secuencias Avanzadas', 'Acceso inmediato en PDF']} cta="COMPRAR AHORA" checkoutLink="https://checkout2.rosetadigital.com/cart/47014673612978:1" data-id="element-75" />

          {/* Combo 3 - Nivel Profesional */}
          <PricingCard title="Combo 3 – Nivel Profesional" price="39.990" priceValue={39990} description="Ideal si además de practicar, querés formarte, enseñar y desarrollar un proyecto personal." isPremium={true} image="https://cdn.magicpatterns.com/uploads/fQsA7yHAC1gKMj3zFn5Wio/ChatGPT_Image_28_ene_2026,_17_23_27.png" features={['Todo lo del Combo 1 (Incluye los bonus gratis)', 'Todo lo del Combo 2', 'Manual Profesional del Instructor', 'Acceso inmediato en PDF']} cta="COMPRAR AHORA" checkoutLink="https://checkout2.rosetadigital.com/cart/47014681018546:1" data-id="element-76" />
        </div>

        <div className="mt-12 text-center max-w-2xl mx-auto" data-id="element-77">
          <p className="text-gray-600 mb-2" data-id="element-78">
            Todos los materiales son digitales y de acceso inmediato.
          </p>
        </div>

        <TrustBadges data-id="element-79" />
      </div>
    </section>;
}